import { useMemo } from 'react'
import * as React from 'react'
import moment from 'moment'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getEffectiveCharterBundleRoute, isAvailableForPurchase } from 'selectors/offerPage/offerPageSelectors'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import FlashCancellationPolicies from 'components/Common/CancellationPolicies/Flash/FlashCancellationPolicies'
import AccommodationSameRoomOptionTile from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTile'
import AccommodationSameRoomOptionTileDetails from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTileDetails'
import AccommodationSameRoomOptionTilePricing from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTilePricing'
import AccommodationSameRoomOptionTileCTA from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTileCTA'
import AccommodationSameRoomOptionTileHeaderArea from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTileHeaderArea'
import FlashHotelOptionPriceDetails from 'components/OfferPage/FlashHotel/FlashHotelOptionPriceDetails'
import FlashHotelOptionCTAs from 'components/OfferPage/FlashHotel/FlashHotelOptionCTAs'
import LuxPlusInclusions from 'luxPlus/components/LuxPlusInclusions'
import InclusionsSection from 'components/OfferPage/FlashHotel/InclusionsSection'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import useLoyaltyOfferPageTargetStayUpgrade from 'components/OfferPage/hooks/useLoyaltyOfferPageTargetStayUpgrade'
import useLoyaltyTargetUpgradePackages from 'loyalty/hooks/useLoyaltyTargetUpgradePackages'
import useLoyaltyOfferPageClearTargetStayUpgrade from 'components/OfferPage/hooks/useLoyaltyOfferPageClearTargetStayUpgrade'
import OfferPageLoyaltyStayUpgradeCallOutBanner from 'components/OfferPage/Loyalty/OfferPageLoyaltyStayUpgradeCallOutBanner'
import OfferPageTierProgressionCallOutBanner from 'components/OfferPage/Loyalty/OfferPageTierProgressionCallOutBanner'
import { useAppSelector } from 'hooks/reduxHooks'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import useUserHasLoyaltyBenefit from 'loyalty/hooks/useUserHasLoyaltyBenefit'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import { AccommodationSameRoomOptionTileSubgrid } from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTileSubgrid'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import SolidStarIcon from 'LuxKit/Icons/solid/SolidStarIcon'
import { useIntl } from 'react-intl'
import getTasteOfGoldPromoCampaign from 'takeovers/tasteOfGoldTakeover/selectors/getTasteOfGoldPromoCampaign'
import isTasteOfGoldEligibleOffer from 'takeovers/tasteOfGoldTakeover/utils/isTasteOfGoldEligibleOffer'
import TasteOfGoldTileBenefitMessage from 'takeovers/tasteOfGoldTakeover/components/TasteOfGoldTileBenefitMessage'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { toBaseFareClass, type FlightsClassTypes } from 'constants/flight'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import useCharterFlightDates from 'hooks/CharterFlights/useCharterFlightDates'
import useNewFlightUpsellExperiment from 'components/OfferPage/FlightDeal/useNewFlightUpsellExperiment'
import { useOfferPageFlightPrice } from 'hooks/useFlightPrice'
import { CHARTER_FLIGHT_DETAILS, MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG } from 'constants/charterFlight'
import { isConfiguredFeederOriginAirportCode } from 'lib/charterFlights/charterBundleOfferPageUtils'
import PackageInclusionsValidityText, { hasStructuredPackageInclusionsValidityText } from 'components/OfferPage/Accommodation/PackageInclusionsValidityText'
import { getInclusionsWithoutPackageValidityText } from 'components/OfferPage/Accommodation/packageInclusionValidityUtils'

const INCLUSIONS_COLLAPSED_MAX_HEIGHT = 80

const InclusionsContainer = styled.div`
  display: grid;
  grid-template-areas: 'base-package' 'target-upgrade-package';
  grid-template-rows: 1fr;
`

const BasePackageInclusions = styled.div`
  grid-area: base-package;
  grid-row: 1;
  opacity: 1;
  transform: translateY(0);
  transition: opacity 300ms ease, transform 300ms ease;
  transition-delay: 300ms;

  &:not(.show) {
    opacity: 0;
    transform: translateY(${rem(16)});
    transition-delay: 0ms;
    pointer-events: none;
  }
`

const TargetUpgradePackageInclusions = styled.div`
  grid-area: target-upgrade-package;
  grid-row: 1;
  opacity: 1;
  transform: translateY(${rem(0)});
  transition: opacity 300ms ease, transform 300ms ease;
  transition-delay: 300ms;

  &:not(.show) {
    opacity: 0;
    transform: translateY(${rem(-16)});
    transition-delay: 0ms;
    pointer-events: none;
  }
`

const SuggestedPackageBannerContainer = styled.div`
  ${mediaQueryUp.tablet} {
    border-inline: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  }
`

function hasPackageInclusionItems(
  luxPlusInclusions: Array<App.PackageInclusion>,
  inclusions: Array<App.PackageInclusion>,
  bonusInclusions: Array<App.PackageInclusion>,
) {
  return luxPlusInclusions.length > 0 || inclusions.length > 0 || bonusInclusions.length > 0
}

interface Props {
  offer: App.HotelOffer
  packageView: App.OfferPackageOptionView
  packagePosition?: number
  checkIn?: string
  duration: number
  rooms: Array<App.Occupants>
  fareClass: FlightsClassTypes
  charterBundleRoute?: App.CharterBundleRoute
  flightOrigin?: App.Airport
  availableForPurchase?: boolean
  roomsBeforeSurcharge?: number
  flights?: boolean
  isInclusionsValidityEnabled: boolean
  inclusionsOpen?: boolean
  onInclusionsToggle?: () => void
  suggestedPackageMessage?: string
  /**
   * Custom content to render in the pricing area.
   * If provided, replaces the default FlashHotelOptionPriceDetails.
   */
  children?: React.ReactNode
}

function FlashHotelRateOption(props: Props) {
  const {
    offer,
    packageView,
    packagePosition,
    checkIn,
    duration,
    rooms,
    fareClass,
    charterBundleRoute,
    flightOrigin,
    availableForPurchase,
    roomsBeforeSurcharge,
    flights,
    isInclusionsValidityEnabled,
    children,
    inclusionsOpen,
    onInclusionsToggle,
    suggestedPackageMessage,
  } = props

  const intl = useIntl()
  const isNewFlightUpsellVariant = useNewFlightUpsellExperiment(!!flights)
  const pkg = packageView.package

  const viewMoreInclusionsText = intl.formatMessage({
    id: 'jcIKjKGxYTZDye5WUFEJ',
    defaultMessage: 'View more inclusions',
    description: 'Expandable link text to reveal additional inclusions in the offer details list',
  })

  const viewLessInclusionsText = intl.formatMessage({
    id: 'n5wlSwDhZXYnvuDemfWI',
    defaultMessage: 'View less inclusions',
    description: 'Collapsible link text to hide additional inclusions and show fewer items in the list',
  })
  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const checkOutMoment = useMemo(() => checkInMoment?.clone().add(duration, 'days'), [checkInMoment, duration])
  const { departDate: charterDepartDate } = useCharterFlightDates({
    checkInDate: checkIn,
    nights: duration,
    charterBundleRoute,
    rooms,
    fareClass,
  })

  const { inclusions, bonusInclusions, luxPlusInclusions } = useOfferInclusions(pkg, { checkIn: checkInMoment, checkOut: checkOutMoment })

  const { hasFeederFlightPriced } = useOfferPageFlightPrice()

  const isCharterBundle = offer.campaignTags?.includes(MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG)
  const charterRouteInfo = CHARTER_FLIGHT_DETAILS['melbourne-maldives']
  const isFeederIncluded = isCharterBundle &&
    isConfiguredFeederOriginAirportCode(charterBundleRoute ?? 'melbourne-maldives', flightOrigin?.code) &&
    hasFeederFlightPriced

  const effectiveInclusions = useMemo(() => {
    if (!isFeederIncluded || !flightOrigin) {
      return inclusions
    }

    const { originAirport, destinationAirport } = charterRouteInfo
    return inclusions.map(inclusion => {
      const isCharterFlightInclusion = inclusion.description.includes(originAirport.cityDesc ?? '')
        && inclusion.description.includes(destinationAirport.cityDesc ?? '')

      if (!isCharterFlightInclusion) {
        return inclusion
      }

      return {
        ...inclusion,
        description: intl.formatMessage(
          {
            id: 'WqwAp2Rk1_YdWyK0xDOI',
            defaultMessage: 'Return flights from {feederCity} to {destination}, via {origin}',
            description: 'Charter bundle feeder route inclusion on hotel search tile when a non-Melbourne feeder departure airport is selected',
          },
          {
            feederCity: flightOrigin.name,
            destination: destinationAirport.cityDesc,
            origin: originAirport.cityDesc,
          },
        ),
      }
    })
  }, [inclusions, isFeederIncluded, charterRouteInfo, flightOrigin, intl])

  const {
    selectedTargetUpgradePackage,
    selectedTargetUpgradeType,
  } = useLoyaltyOfferPageTargetStayUpgrade(pkg)

  const targetUpgradePackages = useLoyaltyTargetUpgradePackages(pkg, offer)
  const loyaltyRoomUpgradePkg = targetUpgradePackages?.room
  const loyaltyPackageUpgradePkg = targetUpgradePackages?.package

  const {
    inclusions: loyaltyRoomUpgradeInclusions,
    bonusInclusions: loyaltyRoomUpgradeBonusInclusions,
    luxPlusInclusions: loyaltyRoomUpgradeLuxPlusInclusions,
  } = useOfferInclusions(loyaltyRoomUpgradePkg, { checkIn: checkInMoment, checkOut: checkOutMoment })

  const {
    inclusions: loyaltyPackageUpgradeInclusions,
    bonusInclusions: loyaltyPackageUpgradeBonusInclusions,
    luxPlusInclusions: loyaltyPackageUpgradeLuxPlusInclusions,
  } = useOfferInclusions(loyaltyPackageUpgradePkg, { checkIn: checkInMoment, checkOut: checkOutMoment })

  const hasInclusions = useMemo(() => {
    return luxPlusInclusions.length > 0 || inclusions.length > 0 || bonusInclusions.length > 0
  }, [luxPlusInclusions, inclusions, bonusInclusions])
  const hasLoyaltyRoomUpgradeInclusions = hasPackageInclusionItems(
    loyaltyRoomUpgradeLuxPlusInclusions,
    loyaltyRoomUpgradeInclusions,
    loyaltyRoomUpgradeBonusInclusions,
  )
  const hasLoyaltyPackageUpgradeInclusions = hasPackageInclusionItems(
    loyaltyPackageUpgradeLuxPlusInclusions,
    loyaltyPackageUpgradeInclusions,
    loyaltyPackageUpgradeBonusInclusions,
  )
  const shouldShowPackageInclusionsValidityText = isInclusionsValidityEnabled && hasStructuredPackageInclusionsValidityText(pkg.roomPolicyDescription)
  const shouldShowLoyaltyRoomUpgradeInclusionsValidityText = isInclusionsValidityEnabled && hasLoyaltyRoomUpgradeInclusions && hasStructuredPackageInclusionsValidityText(loyaltyRoomUpgradePkg?.roomPolicyDescription)
  const shouldShowLoyaltyPackageUpgradeInclusionsValidityText = isInclusionsValidityEnabled && hasLoyaltyPackageUpgradeInclusions && hasStructuredPackageInclusionsValidityText(loyaltyPackageUpgradePkg?.roomPolicyDescription)
  const visibleEffectiveInclusions = useMemo(() => {
    return getInclusionsWithoutPackageValidityText(effectiveInclusions, shouldShowPackageInclusionsValidityText)
  }, [effectiveInclusions, shouldShowPackageInclusionsValidityText])
  const visibleBonusInclusions = useMemo(() => {
    return getInclusionsWithoutPackageValidityText(bonusInclusions, shouldShowPackageInclusionsValidityText)
  }, [bonusInclusions, shouldShowPackageInclusionsValidityText])
  const visibleLoyaltyRoomUpgradeInclusions = useMemo(() => {
    return getInclusionsWithoutPackageValidityText(loyaltyRoomUpgradeInclusions, shouldShowLoyaltyRoomUpgradeInclusionsValidityText)
  }, [loyaltyRoomUpgradeInclusions, shouldShowLoyaltyRoomUpgradeInclusionsValidityText])
  const visibleLoyaltyRoomUpgradeBonusInclusions = useMemo(() => {
    return getInclusionsWithoutPackageValidityText(loyaltyRoomUpgradeBonusInclusions, shouldShowLoyaltyRoomUpgradeInclusionsValidityText)
  }, [loyaltyRoomUpgradeBonusInclusions, shouldShowLoyaltyRoomUpgradeInclusionsValidityText])
  const visibleLoyaltyPackageUpgradeInclusions = useMemo(() => {
    return getInclusionsWithoutPackageValidityText(loyaltyPackageUpgradeInclusions, shouldShowLoyaltyPackageUpgradeInclusionsValidityText)
  }, [loyaltyPackageUpgradeInclusions, shouldShowLoyaltyPackageUpgradeInclusionsValidityText])
  const visibleLoyaltyPackageUpgradeBonusInclusions = useMemo(() => {
    return getInclusionsWithoutPackageValidityText(loyaltyPackageUpgradeBonusInclusions, shouldShowLoyaltyPackageUpgradeInclusionsValidityText)
  }, [loyaltyPackageUpgradeBonusInclusions, shouldShowLoyaltyPackageUpgradeInclusionsValidityText])
  const selectedOccupancy = rooms.length === 1 ? rooms[0] : undefined

  const isLoyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const isLoyaltyRoomUpgradesAvailable = useUserHasLoyaltyBenefit('hotel_upgrade')

  const currency = useAppSelector(state => state.geo.currentCurrency)
  const isTasteOfGoldActive = !!useAppSelector(getTasteOfGoldPromoCampaign)
  const showTasteOfGoldBenefit = useMemo(() => {
    return isTasteOfGoldActive && isTasteOfGoldEligibleOffer(
      offer,
      currency,
      offer.property?.geoData?.countryCode?.toLowerCase() !== 'au',
      pkg.price,
    )
  }, [isTasteOfGoldActive, offer, currency, pkg.price])

  useLoyaltyOfferPageClearTargetStayUpgrade(packageView)

  return (
    <AccommodationSameRoomOptionTileSubgrid>
      {suggestedPackageMessage && <SuggestedPackageBannerContainer>
        <MessageBanner kind="promotional-primary" icon={<SolidStarIcon />}>{suggestedPackageMessage}</MessageBanner>
      </SuggestedPackageBannerContainer>}
      <AccommodationSameRoomOptionTile>
        {isLoyaltyEnabled && (
          <AccommodationSameRoomOptionTileHeaderArea>
            {isLoyaltyRoomUpgradesAvailable && (
              <OfferPageLoyaltyStayUpgradeCallOutBanner packageView={packageView} />
            )}
            <OfferPageTierProgressionCallOutBanner
              offer={offer}
              packageView={packageView}
              checkIn={checkIn}
              duration={duration}
              hasLoyaltyAccount={hasLoyaltyAccount}
            />
          </AccommodationSameRoomOptionTileHeaderArea>
        )}
        <AccommodationSameRoomOptionTileDetails
          packageNumber={packagePosition}
          optionName={pkg.name}
          selectedLoyaltyTargetUpgradePackage={selectedTargetUpgradePackage}
        >
          {availableForPurchase && !selectedTargetUpgradePackage && pkg.roomRate && (
            <FlashCancellationPolicies
              checkInDate={checkIn}
              cancellationReferenceDate={charterBundleRoute ? charterDepartDate : undefined}
              propertyTimezoneOffset={offer.property?.timezoneOffset}
              cancellationPolicy={pkg.roomRate.cancellationPolicy}
            />
          )}
          {availableForPurchase && selectedTargetUpgradePackage?.roomRate && (
            <FlashCancellationPolicies
              checkInDate={checkIn}
              cancellationReferenceDate={charterBundleRoute ? charterDepartDate : undefined}
              propertyTimezoneOffset={offer.property?.timezoneOffset}
              cancellationPolicy={selectedTargetUpgradePackage.roomRate.cancellationPolicy}
            />
          )}
          {hasInclusions && (
            <InclusionsContainer>
              <BasePackageInclusions className={clsx({ 'show': !selectedTargetUpgradePackage })}>
                <LargeContentToggle
                  maxHeight={INCLUSIONS_COLLAPSED_MAX_HEIGHT}
                  showMoreText={viewMoreInclusionsText}
                  showLessText={viewLessInclusionsText}
                  open={inclusionsOpen}
                  onToggle={onInclusionsToggle}
                >
                  <VerticalSpacer gap={8}>
                    {!!luxPlusInclusions.length && (
                      <LuxPlusInclusions
                        luxPlusInclusions={luxPlusInclusions}
                        accommodationOffer={offer}
                        hideUpsellModal
                      />
                    )}
                    {showTasteOfGoldBenefit && <TasteOfGoldTileBenefitMessage />}
                    <InclusionsSection
                      offer={offer}
                      inclusions={visibleEffectiveInclusions}
                      bonusInclusions={visibleBonusInclusions}
                      showReturnFlightsInclusion={isNewFlightUpsellVariant && flights}
                      rooms={rooms}
                    />
                  </VerticalSpacer>
                </LargeContentToggle>
                {shouldShowPackageInclusionsValidityText && (
                  <PackageInclusionsValidityText policyDescription={pkg.roomPolicyDescription} selectedOccupancy={selectedOccupancy} />
                )}
              </BasePackageInclusions>
              {loyaltyRoomUpgradePkg && (
                <TargetUpgradePackageInclusions className={clsx({ 'show': selectedTargetUpgradeType === 'room' })}>
                  <LargeContentToggle
                    maxHeight={INCLUSIONS_COLLAPSED_MAX_HEIGHT}
                    showMoreText={viewMoreInclusionsText}
                    showLessText={viewLessInclusionsText}
                    open={inclusionsOpen}
                    onToggle={onInclusionsToggle}
                  >
                    <VerticalSpacer gap={8}>
                      {!!loyaltyRoomUpgradeLuxPlusInclusions.length && (
                        <LuxPlusInclusions
                          luxPlusInclusions={loyaltyRoomUpgradeLuxPlusInclusions}
                          accommodationOffer={offer}
                          hideUpsellModal
                        />
                      )}
                      {showTasteOfGoldBenefit && <TasteOfGoldTileBenefitMessage />}
                      <InclusionsSection
                        offer={offer}
                        inclusions={visibleLoyaltyRoomUpgradeInclusions}
                        bonusInclusions={visibleLoyaltyRoomUpgradeBonusInclusions}
                        showReturnFlightsInclusion={isNewFlightUpsellVariant && flights}
                        rooms={rooms}
                      />
                    </VerticalSpacer>
                  </LargeContentToggle>
                  {shouldShowLoyaltyRoomUpgradeInclusionsValidityText && (
                    <PackageInclusionsValidityText policyDescription={loyaltyRoomUpgradePkg.roomPolicyDescription} selectedOccupancy={selectedOccupancy} />
                  )}
                </TargetUpgradePackageInclusions>
              )}
              {loyaltyPackageUpgradePkg && (
                <TargetUpgradePackageInclusions className={clsx({ 'show': selectedTargetUpgradeType === 'package' })}>
                  <LargeContentToggle
                    maxHeight={INCLUSIONS_COLLAPSED_MAX_HEIGHT}
                    showMoreText={viewMoreInclusionsText}
                    showLessText={viewLessInclusionsText}
                    open={inclusionsOpen}
                    onToggle={onInclusionsToggle}
                  >
                    <VerticalSpacer gap={8}>
                      {!!loyaltyPackageUpgradeLuxPlusInclusions.length && (
                        <LuxPlusInclusions
                          luxPlusInclusions={loyaltyPackageUpgradeLuxPlusInclusions}
                          accommodationOffer={offer}
                          hideUpsellModal
                        />
                      )}
                      {showTasteOfGoldBenefit && <TasteOfGoldTileBenefitMessage />}
                      <InclusionsSection
                        offer={offer}
                        inclusions={visibleLoyaltyPackageUpgradeInclusions}
                        bonusInclusions={visibleLoyaltyPackageUpgradeBonusInclusions}
                        showReturnFlightsInclusion={isNewFlightUpsellVariant && flights}
                        rooms={rooms}
                      />
                    </VerticalSpacer>
                  </LargeContentToggle>
                  {shouldShowLoyaltyPackageUpgradeInclusionsValidityText && (
                    <PackageInclusionsValidityText policyDescription={loyaltyPackageUpgradePkg.roomPolicyDescription} selectedOccupancy={selectedOccupancy} />
                  )}
                </TargetUpgradePackageInclusions>
              )}
            </InclusionsContainer>
          )}
        </AccommodationSameRoomOptionTileDetails>

        <AccommodationSameRoomOptionTilePricing>
          {!children && availableForPurchase && <FlashHotelOptionPriceDetails packageView={packageView} roomsBeforeSurcharge={roomsBeforeSurcharge} />}
        </AccommodationSameRoomOptionTilePricing>

        <AccommodationSameRoomOptionTileCTA>
          {children}
          {!children && availableForPurchase && <FlashHotelOptionCTAs packageView={packageView} />}
        </AccommodationSameRoomOptionTileCTA>
      </AccommodationSameRoomOptionTile>
    </AccommodationSameRoomOptionTileSubgrid>
  )
}

type MappedProps = Pick<Props,
  | 'offer'
  | 'checkIn'
  | 'duration'
  | 'rooms'
  | 'fareClass'
  | 'charterBundleRoute'
  | 'flightOrigin'
  | 'availableForPurchase'
  | 'flights'
  | 'isInclusionsValidityEnabled'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  offer: state.offer as App.HotelOffer,
  checkIn: state.checkInDate,
  duration: state.duration,
  rooms: state.rooms,
  fareClass: toBaseFareClass(state.fareClass),
  charterBundleRoute: getEffectiveCharterBundleRoute(state),
  flightOrigin: state.airport,
  availableForPurchase: isAvailableForPurchase(state, appState),
  flights: state.flights,
  isInclusionsValidityEnabled: !!appState.optimizely.optimizelyFeatureFlags[OptimizelyFeatureFlags.croInclusionValidity],
}), FlashHotelRateOption)
